import { useEffect, useState } from 'react';
import type { Moment } from 'moment';
import { useLoyaltyClubStore } from '~contexts/mobx';
import { convertUtcToLocalMoment } from '~utils/timezone';
import {
    analyticsToday,
    campaignRange,
    CampaignWindow,
} from '~containers/analytics/kpis/periods';
import { ROAD_TO_REWARDS_CAMPAIGN_ID } from '~containers/analytics/kpis/sources/road-to-rewards-campaign';
import type { KpiCampaignContext } from '~containers/analytics/kpis/types';

/**
 * The Road to Rewards campaign window, read once per page off the singleton
 * club and handed to both readers: the `Campaign` period capsule and every
 * card's to-date total.
 */
const DATE_FORMAT = 'YYYY-MM-DD';

const CLUB_MISSING =
    'No Road to Rewards campaign club is configured in this environment.';

const CLUB_MISDATED =
    'The Road to Rewards campaign club is configured, but its dates cannot be read.';

const CLUB_FAILED =
    'The Road to Rewards campaign could not be loaded, so its totals cannot be framed.';

export interface CampaignWindowState {
    /** For the period capsule, which takes the campaign's own bounds. */
    campaign: CampaignWindow | undefined;
    /** For the cards, which take the resolved to-date window or the reason
     * there is none. */
    context: KpiCampaignContext;
}

/** The club stores its dates as UTC instants and its own timezone beside them,
 * so the campaign's calendar day is read in the campaign's zone. */
const campaignDay = (
    date: string | Moment,
    timezone: string
): string | null => {
    const local = convertUtcToLocalMoment(date, timezone);
    return local?.isValid() ? local.format(DATE_FORMAT) : null;
};

export const useCampaignWindow = (): CampaignWindowState => {
    const loyaltyClubStore = useLoyaltyClubStore();
    const [state, setState] = useState<CampaignWindowState>({
        campaign: undefined,
        context: { status: 'loading' },
    });

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            try {
                const club = await loyaltyClubStore.fetchRoadToRewardsClub();
                if (cancelled) return;
                if (!club) {
                    setState({
                        campaign: undefined,
                        context: {
                            status: 'unavailable',
                            reason: CLUB_MISSING,
                        },
                    });
                    return;
                }
                const startDate = campaignDay(club.startDate, club.timezone);
                // `undefined` is no date configured; `null` is one that will
                // not parse. Collapsing the two would read a misconfigured
                // club as a campaign still running.
                // Only an absent date is "no end configured"; a blank string
                // is a configured value that cannot parse, and reads as null.
                const day = (date: string | null | undefined) =>
                    date == null ? undefined : campaignDay(date, club.timezone);
                const visitEnd = day(club.endDate);
                const graceEnd = day(club.graceEndDate);
                // Claims outlive visits, and it is the claim that creates a
                // coupon grant, so the grace end is the campaign's reporting
                // bound wherever one is set.
                const endDate = graceEnd ?? visitEnd;
                // The API enforces this on create but not on update, and the
                // configuration page does not either, so a grace end dragged
                // before the visit end reaches us and would shorten every total.
                const graceBeforeEnd =
                    !!graceEnd && !!visitEnd && graceEnd < visitEnd;
                const endBeforeStart =
                    !!startDate && !!endDate && endDate < startDate;
                if (
                    !startDate ||
                    visitEnd === null ||
                    graceEnd === null ||
                    graceBeforeEnd ||
                    endBeforeStart
                ) {
                    setState({
                        campaign: undefined,
                        context: {
                            status: 'unavailable',
                            reason: CLUB_MISDATED,
                        },
                    });
                    return;
                }
                const campaign: CampaignWindow = {
                    startDate,
                    ...(endDate && { endDate }),
                };
                setState({
                    campaign,
                    context: {
                        status: 'ready',
                        runsToday:
                            !campaign.endDate ||
                            campaign.endDate >= analyticsToday(),
                        toDate: {
                            ...campaignRange(campaign, analyticsToday()),
                            preset: {
                                period: 'campaign',
                                campaignId: ROAD_TO_REWARDS_CAMPAIGN_ID,
                            },
                        },
                    },
                });
            } catch {
                if (cancelled) return;
                setState({
                    campaign: undefined,
                    context: { status: 'unavailable', reason: CLUB_FAILED },
                });
            }
        };

        void load();
        return () => {
            cancelled = true;
        };
    }, [loyaltyClubStore]);

    return state;
};
